/**
 * BOM creation sandbox — start from typed/spoken brief, review match bands,
 * commit root, park for handoff.
 */

import { useCallback, useEffect, useState } from "react";
import {
  ghostBtnClass,
  mfgPrimaryBtnClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingModelsDictateMicButton } from "./ManufacturingModelsDictateMicButton";
import { ManufacturingModelsBomSandboxMembers } from "./ManufacturingModelsBomSandboxMembers";
import {
  cancelManufacturingBomAuthoringSession,
  commitManufacturingBomAuthoringSessionRoot,
  completeManufacturingBomAuthoringSession,
  createManufacturingBomAuthoringSession,
  listManufacturingBomAuthoringSessions,
  parkManufacturingBomAuthoringSession,
  resumeManufacturingBomAuthoringSession,
  updateManufacturingBomAuthoringSessionDraft,
  type BomAuthoringMember,
  type BomAuthoringSessionSummary,
  type BomMatchCandidate,
} from "@/lib/manufacturing/bomAuthoringSessionsClient";
import {
  listManufacturingRecipeGroups,
  type MfgRecipeGroupSummary,
} from "@/lib/manufacturing/recipeGroupsClient";

export function ManufacturingModelsBomSandboxModal({
  onClose,
  onCommitted,
  initialRecipeGroupId,
}: {
  onClose: () => void;
  onCommitted: (recipeId: string) => void;
  initialRecipeGroupId?: string | null;
}) {
  const [brief, setBrief] = useState("");
  const [source, setSource] = useState<"typed" | "spoken" | "mixed">("typed");
  const [groups, setGroups] = useState<MfgRecipeGroupSummary[]>([]);
  const [groupId, setGroupId] = useState(initialRecipeGroupId ?? "");
  const [session, setSession] = useState<BomAuthoringSessionSummary | null>(null);
  const [handoff, setHandoff] = useState<BomAuthoringSessionSummary[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshHandoff = useCallback(async () => {
    const r = await listManufacturingBomAuthoringSessions({ handoff_only: true });
    if (r.ok) setHandoff(r.data.sessions);
  }, []);

  useEffect(() => {
    if (initialRecipeGroupId) setGroupId(initialRecipeGroupId);
  }, [initialRecipeGroupId]);

  useEffect(() => {
    void (async () => {
      const [g, h] = await Promise.all([
        listManufacturingRecipeGroups(),
        listManufacturingBomAuthoringSessions({ handoff_only: true }),
      ]);
      if (g.ok) setGroups(g.data.groups);
      if (h.ok) setHandoff(h.data.sessions);
    })();
  }, []);

  async function startFromBrief(text: string, nextSource: "typed" | "spoken" | "mixed") {
    const trimmed = text.trim();
    if (!trimmed) {
      setError("Enter or dictate a brief describing the product and its members.");
      return;
    }
    setBusy(true);
    setError(null);
    const r = await createManufacturingBomAuthoringSession({
      brief_text: trimmed,
      source: nextSource,
      recipe_group_id: groupId || null,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setSession(r.data.session);
    setSource(nextSource);
    void refreshHandoff();
  }

  function patchMember(index: number, patch: Partial<BomAuthoringMember>) {
    if (!session) return;
    const members = session.draft.members.map((m, i) =>
      i === index ? { ...m, ...patch } : m
    );
    setSession({ ...session, draft: { ...session.draft, members } });
  }

  function pickCandidate(index: number, candidate: BomMatchCandidate) {
    patchMember(index, {
      band: "high",
      auto_attach: candidate,
      candidates: [candidate],
    });
  }

  async function saveDraftAndPark() {
    if (!session) return;
    setBusy(true);
    setError(null);
    const upd = await updateManufacturingBomAuthoringSessionDraft({
      session_id: session.id,
      draft: session.draft,
    });
    if (!upd.ok) {
      setBusy(false);
      setError(upd.error);
      return;
    }
    const parked = await parkManufacturingBomAuthoringSession({
      session_id: session.id,
      draft: upd.data.session.draft,
    });
    setBusy(false);
    if (!parked.ok) {
      setError(parked.error);
      return;
    }
    setSession(parked.data.session);
    void refreshHandoff();
  }

  async function commitRoot() {
    if (!session) return;
    setBusy(true);
    setError(null);
    const upd = await updateManufacturingBomAuthoringSessionDraft({
      session_id: session.id,
      draft: session.draft,
    });
    if (!upd.ok) {
      setBusy(false);
      setError(upd.error);
      return;
    }
    const committed = await commitManufacturingBomAuthoringSessionRoot(session.id);
    if (!committed.ok) {
      setBusy(false);
      setError(committed.error);
      return;
    }
    const done = await completeManufacturingBomAuthoringSession(session.id);
    setBusy(false);
    if (!done.ok) {
      setError(done.error);
      onCommitted(committed.data.recipe_id);
      return;
    }
    onCommitted(committed.data.recipe_id);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-950">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50">
              Start BOM from brief
            </h2>
            <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
              Match existing assemblies, part uses, and variants — or leave placeholders.
            </p>
          </div>
          <button type="button" className={ghostBtnClass} onClick={onClose} disabled={busy}>
            Close
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
              {error}
            </div>
          ) : null}

          {!session ? (
            <>
              {handoff.length > 0 ? (
                <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Open / parked (handoff)
                  </div>
                  <ul className="mt-2 space-y-2">
                    {handoff.map((s) => (
                      <li
                        key={s.id}
                        className="flex flex-wrap items-center justify-between gap-2 text-sm"
                      >
                        <span className="min-w-0 flex-1 truncate">
                          <span className="font-medium">{s.draft?.name || "Untitled"}</span>
                          <span className="text-slate-500"> · {s.status}</span>
                        </span>
                        <button
                          type="button"
                          className={mfgSecondaryBtnClass}
                          disabled={busy}
                          onClick={() => {
                            void (async () => {
                              setBusy(true);
                              const r = await resumeManufacturingBomAuthoringSession(s.id);
                              setBusy(false);
                              if (r.ok) setSession(r.data.session);
                              else setError(r.error);
                            })();
                          }}
                        >
                          Resume
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <label className="block text-sm">
                <span className="text-slate-600 dark:text-slate-300">Recipe group (optional)</span>
                <select
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900"
                  value={groupId}
                  onChange={(e) => setGroupId(e.target.value)}
                >
                  <option value="">— None —</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name} ({g.code})
                    </option>
                  ))}
                </select>
              </label>

              <label className="block text-sm">
                <span className="text-slate-600 dark:text-slate-300">Brief</span>
                <textarea
                  className="mt-1 min-h-[120px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900"
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  placeholder="Describe the model to build — size, assemblies to reuse, packages, and any variants…"
                />
              </label>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className={mfgPrimaryBtnClass}
                  disabled={busy}
                  onClick={() => void startFromBrief(brief, source === "spoken" ? "mixed" : "typed")}
                >
                  {busy ? "Extracting…" : "Extract & match"}
                </button>
                <ManufacturingModelsDictateMicButton
                  onComplete={(t) => {
                    setBrief(t);
                    setSource("spoken");
                    void startFromBrief(t, "spoken");
                  }}
                  extractLabel="Dictate & extract →"
                  disabled={busy}
                />
              </div>
            </>
          ) : (
            <>
              <div className="rounded-xl border border-slate-200 p-3 text-sm dark:border-slate-700">
                <div className="font-medium text-slate-900 dark:text-slate-50">
                  {session.draft.name}{" "}
                  <span className="font-normal text-slate-500">({session.draft.code})</span>
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  Status {session.status}
                  {session.root_recipe_id ? ` · recipe ${session.root_recipe_id.slice(0, 8)}…` : ""}
                  {session.draft.stack?.length
                    ? ` · stack depth ${session.draft.stack.length}`
                    : ""}
                </div>
              </div>

              <ManufacturingModelsBomSandboxMembers
                members={session.draft.members}
                onPickCandidate={pickCandidate}
                onLeavePlaceholder={(idx) =>
                  patchMember(idx, { band: "none", auto_attach: null })
                }
              />
            </>
          )}
        </div>

        {session ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-4 py-3 dark:border-slate-700">
            <button
              type="button"
              className={ghostBtnClass}
              disabled={busy}
              onClick={() => {
                void (async () => {
                  setBusy(true);
                  await cancelManufacturingBomAuthoringSession(session.id);
                  setBusy(false);
                  setSession(null);
                  void refreshHandoff();
                })();
              }}
            >
              Cancel session
            </button>
            <button
              type="button"
              className={mfgSecondaryBtnClass}
              disabled={busy}
              onClick={() => void saveDraftAndPark()}
            >
              Park for handoff
            </button>
            {!session.root_recipe_id ? (
              <button
                type="button"
                className={mfgPrimaryBtnClass}
                disabled={busy}
                onClick={() => void commitRoot()}
              >
                {busy ? "Saving…" : "Create recipe"}
              </button>
            ) : (
              <button
                type="button"
                className={mfgPrimaryBtnClass}
                disabled={busy}
                onClick={() => onCommitted(session.root_recipe_id!)}
              >
                Open recipe
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
